/**
 * The settings and gallery sheets.
 *
 * Kept apart from the capture flow because none of it is on the critical path:
 * the app has to be usable the second the camera comes up, and everything here
 * is what somebody reaches for afterwards.
 */
import { el, all, on, formatBytes, formatWhen } from '../util/dom';
import { applyTheme } from './theme';
import { QUALITY, type QualityName } from './quality';
import type { SettingsStore, Settings } from './settings';
import { allShots, clearGallery, deleteShot, describeShot, galleryUsage, getShot, listShots, type Shot } from './gallery';
import { shareImage } from './share';
import { haptic, toast } from './feedback';
import type { SheetHost } from './sheet';

export interface PanelDeps {
  sheets: SheetHost;
  settings: SettingsStore;
  /** Called when a change needs the camera or the buffer rebuilt. */
  onQualityChange: (quality: QualityName) => void;
  /** Called when the person picks a different lens. */
  onDeviceChange: (deviceId: string) => void;
  /** The cameras currently available, for the lens picker. */
  listDevices: () => Promise<MediaDeviceInfo[]>;
  /** The lens in use, so the picker can show it selected. */
  currentDeviceId: () => string | null;
  /** Which eraser this device gets, and whether it is already downloaded. */
  eraserStatus: () => Promise<{ label: string; ready: boolean }>;
  /** Download the eraser now rather than on the first photograph. */
  fetchEraser: (onProgress: (fraction: number) => void) => Promise<void>;
}

/** Wire a segmented control to a settings key. */
function segment<K extends keyof Settings>(
  container: HTMLElement,
  attribute: string,
  settings: SettingsStore,
  key: K,
  onPick?: (value: Settings[K]) => void,
): void {
  const buttons = all<HTMLButtonElement>('button', container);
  const paint = () => {
    for (const b of buttons) {
      b.setAttribute('aria-checked', String(b.dataset[attribute] === String(settings.get(key))));
    }
  };
  for (const b of buttons) {
    on(b, 'click', () => {
      // Numeric settings (the timer) arrive from the markup as strings.
      const raw = b.dataset[attribute] ?? '';
      const value = (typeof settings.get(key) === 'number' ? Number(raw) : raw) as Settings[K];
      settings.set(key, value);
      paint();
      haptic('tap');
      onPick?.(value);
    });
  }
  // Arrow keys are what a radio group is expected to answer to.
  on(container, 'keydown', (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
    if (index < 0) return;
    e.preventDefault();
    const step = e.key === 'ArrowRight' ? 1 : -1;
    const next = buttons[(index + step + buttons.length) % buttons.length];
    next.focus();
    next.click();
  });
  settings.subscribe(paint);
  paint();
}

/** Wire a checkbox to a settings key. */
function toggle<K extends keyof Settings>(
  input: HTMLInputElement,
  settings: SettingsStore,
  key: K,
  onPick?: (value: boolean) => void,
): void {
  const paint = () => {
    input.checked = Boolean(settings.get(key));
  };
  on(input, 'change', () => {
    settings.set(key, input.checked as Settings[K]);
    onPick?.(input.checked);
  });
  settings.subscribe(paint);
  paint();
}

export function mountSettingsPanel(deps: PanelDeps): { open: () => void } {
  const sheet = el('settingsSheet');
  const { settings } = deps;

  segment(el('qualitySeg'), 'quality', settings, 'quality', (value) =>
    deps.onQualityChange(value as QualityName),
  );
  segment(el('formatSeg'), 'format', settings, 'format');
  segment(el('timerSeg'), 'timer', settings, 'timer');
  toggle(el<HTMLInputElement>('setRaise'), settings, 'raiseToShoot');

  const eraserNote = el('eraserNote');
  const eraserFetch = el<HTMLButtonElement>('eraserFetch');
  async function paintEraser(): Promise<void> {
    const status = await deps.eraserStatus();
    eraserNote.textContent = status.label;
    eraserFetch.hidden = status.ready;
  }
  on(eraserFetch, 'click', async () => {
    eraserFetch.disabled = true;
    try {
      await deps.fetchEraser((f) => {
        eraserNote.textContent = `Downloading… ${Math.round(f * 100)}%`;
      });
      toast('The eraser is on this device now.', 'good');
    } catch {
      toast('The download did not finish. Try again on a better connection.', 'warn');
    } finally {
      eraserFetch.disabled = false;
      void paintEraser();
    }
  });
  segment(el('themeSeg'), 'theme', settings, 'theme', (value) => applyTheme(value as Settings['theme']));

  toggle(el<HTMLInputElement>('setKeep'), settings, 'keepGallery');
  toggle(el<HTMLInputElement>('setWatermark'), settings, 'watermark');
  toggle(el<HTMLInputElement>('setHaptics'), settings, 'haptics');
  toggle(el<HTMLInputElement>('setSound'), settings, 'shutterSound');

  const qualityNote = el('qualityNote');
  const paintQuality = () => {
    const q = QUALITY[settings.get('quality')];
    qualityNote.textContent = `Up to ${q.longEdge} px on the long edge, the sharpest of the last ${q.frames} frames.`;
  };
  settings.subscribe(paintQuality);
  paintQuality();

  const deviceSelect = el<HTMLSelectElement>('deviceSelect');
  on(deviceSelect, 'change', () => deps.onDeviceChange(deviceSelect.value));

  async function paintDevices(): Promise<void> {
    const devices = await deps.listDevices();
    const current = deps.currentDeviceId();
    deviceSelect.replaceChildren();
    if (devices.length === 0) {
      const option = new Option('No cameras found', '');
      option.disabled = true;
      deviceSelect.append(option);
      deviceSelect.disabled = true;
      return;
    }
    deviceSelect.disabled = false;
    devices.forEach((d, i) => {
      // Labels are empty until permission has been granted at least once.
      const option = new Option(d.label || `Camera ${i + 1}`, d.deviceId);
      option.selected = d.deviceId === current;
      deviceSelect.append(option);
    });
  }

  const storageNote = el('storageNote');
  async function paintStorage(): Promise<void> {
    const shots = await listShots().catch(() => []);
    const usage = await galleryUsage();
    const kept = shots.length === 1 ? '1 shot' : `${shots.length} shots`;
    storageNote.textContent = usage
      ? `${kept} kept — ${formatBytes(usage.used)} of ${formatBytes(usage.quota)} available on this device.`
      : `${kept} kept on this device.`;
  }

  on(el('resetBtn'), 'click', () => {
    settings.reset();
    applyTheme(settings.get('theme'));
    toast('Settings reset.', 'good');
  });

  on(el('clearBtn'), 'click', async () => {
    if (!window.confirm('Delete every photograph kept on this device? This cannot be undone.')) return;
    await clearGallery().catch(() => undefined);
    await paintStorage();
    toast('All shots deleted.', 'good');
  });

  on(el('howBtn'), 'click', () => deps.sheets.open(el('aboutSheet')));

  return {
    open() {
      deps.sheets.open(sheet);
      void paintDevices();
      void paintStorage();
      void paintEraser();
    },
  };
}

export function mountGalleryPanel(deps: {
  sheets: SheetHost;
  settings: SettingsStore;
}): { open: () => void } {
  const sheet = el('gallerySheet');
  const grid = el<HTMLUListElement>('galleryGrid');
  const empty = el('galleryEmpty');

  const viewer = el('viewerSheet');
  const viewerImg = el<HTMLImageElement>('viewerImg');
  const viewerMeta = el('viewerMeta');
  const viewerBefore = el<HTMLButtonElement>('viewerBefore');

  // Two independent sets of object URLs: the grid's live for as long as the
  // grid is on screen, the viewer's only for the shot being looked at. They are
  // kept apart because opening the viewer closes the grid, and a shared pool
  // would revoke the very image the viewer was about to show.
  const gridUrls: string[] = [];
  const viewerUrls: string[] = [];
  const release = (urls: string[]) => {
    for (const url of urls) URL.revokeObjectURL(url);
    urls.length = 0;
  };
  const releaseGrid = () => release(gridUrls);
  const track = (blob: Blob, into: string[]) => {
    const url = URL.createObjectURL(blob);
    into.push(url);
    return url;
  };

  let openShot: { id: string; after: string; before: string; takenAt: number } | null = null;

  async function showShot(shot: Shot): Promise<void> {
    release(viewerUrls);
    openShot = {
      id: shot.id,
      after: track(shot.after, viewerUrls),
      before: track(shot.before, viewerUrls),
      takenAt: shot.takenAt,
    };
    viewerImg.src = openShot.after;
    viewerBefore.setAttribute('aria-pressed', 'false');
    viewerBefore.textContent = 'Show before';
    const level = shot.tiltDegrees ? `, levelled ${Math.abs(shot.tiltDegrees).toFixed(1)}°` : '';
    viewerMeta.textContent = `${describeShot(shot)}${level}. ${shot.width}×${shot.height}, ${formatWhen(shot.takenAt)}.`;
    deps.sheets.open(viewer, () => {
      release(viewerUrls);
      deps.sheets.open(sheet, releaseGrid);
      void paint();
    });
  }

  on(viewerBefore, 'click', () => {
    if (!openShot) return;
    const showingBefore = viewerBefore.getAttribute('aria-pressed') === 'true';
    viewerImg.src = showingBefore ? openShot.after : openShot.before;
    viewerBefore.setAttribute('aria-pressed', String(!showingBefore));
    viewerBefore.textContent = showingBefore ? 'Show before' : 'Show after';
  });

  on(el('viewerShare'), 'click', async () => {
    if (!openShot) return;
    const shot = await getShot(openShot.id);
    if (!shot) return;
    const outcome = await shareImage(shot.after, deps.settings.get('format'), shot.takenAt);
    if (outcome === 'downloaded') toast('Saved to your downloads.', 'good');
  });

  on(el('viewerDelete'), 'click', async () => {
    if (!openShot) return;
    if (!window.confirm('Delete this photograph?')) return;
    await deleteShot(openShot.id).catch(() => undefined);
    openShot = null;
    // Closing the viewer reopens the grid and repaints it, so the deleted shot
    // disappears without a second pass over the store.
    deps.sheets.close();
    toast('Deleted.', 'good');
  });

  async function paint(): Promise<void> {
    const shots = await allShots().catch(() => []);
    releaseGrid();
    grid.replaceChildren();
    empty.hidden = shots.length > 0;

    for (const shot of shots) {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      const figure = document.createElement('figure');
      figure.style.margin = '0';
      const img = document.createElement('img');
      img.src = track(shot.thumb ?? shot.after, gridUrls);
      img.alt = `Taken ${formatWhen(shot.takenAt)}. ${describeShot(shot)}.`;
      // Not lazy: these are object URLs for blobs already in memory, so there
      // is nothing to defer, and a lazily-loaded image inside a sheet that was
      // hidden when its `src` was set can sit there never deciding to load.
      img.decoding = 'async';
      const caption = document.createElement('figcaption');
      caption.textContent = formatWhen(shot.takenAt);
      figure.append(img, caption);
      button.append(figure);
      on(button, 'click', () => void showShot(shot));
      li.append(button);
      grid.append(li);
    }
  }

  return {
    open() {
      deps.sheets.open(sheet, releaseGrid);
      void paint();
    },
  };
}
